import { useCallback, useEffect, useState } from 'react';

/**
 * @file web/src/utils/viewMode.ts
 * @description Tiny localStorage-backed hook for the search results
 * card/table view toggle. The selection survives reloads + future
 * sessions; falls back to 'cards' (the previous default) when no
 * choice is recorded.
 *
 * Intentionally tiny — keeping this off zustand because no other
 * surface needs to read or react to the value.
 */

export type ListingViewMode = 'cards' | 'table';

const STORAGE_KEY = 'roundrecon.searchResults.viewMode';
const DEFAULT: ListingViewMode = 'cards';

/**
 * Read the persisted view-mode if it's still a value we understand.
 * Returns the default when the storage entry is missing, corrupted,
 * or carries an unknown enum.
 *
 * @returns {ListingViewMode}
 */
function readInitial(): ListingViewMode {
  if (typeof window === 'undefined') return DEFAULT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'cards' || raw === 'table') return raw;
  } catch {
    // Private mode / disabled storage — fall through to default.
  }
  return DEFAULT;
}

/**
 * React hook returning the current view-mode and a setter that
 * persists to localStorage on change.
 *
 * @returns {[ListingViewMode, (next: ListingViewMode) => void]}
 */
export function useListingViewMode(): [ListingViewMode, (next: ListingViewMode) => void] {
  const [mode, setMode] = useState<ListingViewMode>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Best-effort persistence — never throw from the setter.
    }
  }, [mode]);

  const set = useCallback((next: ListingViewMode) => {
    setMode(next);
  }, []);

  return [mode, set];
}
